import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import OptimizedImage from '../ui/OptimizedImage';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import { StarIcon, LeafIcon, IconRefresh, LoadingSpinner } from '../ui/Icons';
import PlantAttributeGrid from './PlantAttributeGrid';
import BotaniqChat from './BotaniqChat';
import SharePlantButton from './SharePlantButton';
import AdminPlantEditor from '../admin/AdminPlantEditor';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../utils/api';

// Helper: shows shortened (or full) text with a toggle
const TextWithExpand = ({ short, full, className = '' }) => {
    const [expanded, setExpanded] = React.useState(false);
    const hasShort = short && short.trim();
    const displayText = (!expanded && hasShort) ? short : (full || short || '');
    const canToggle = hasShort && full && full !== short;
    return (
        <span className={className}>
            {displayText}
            {canToggle && (
                <button
                    onClick={() => setExpanded(e => !e)}
                    className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-plantlix-500 hover:text-plantlix-700 dark:text-plantlix-400 transition-colors"
                >
                    {expanded ? '↑ weniger' : '+ mehr'}
                </button>
            )}
        </span>
    );
};

const PlantDetailModal = ({ plant, userPlantData, categories, onClose, onUpdateUserPlantData, appVersion = 'v2.0' }) => {
    const { user, fetchWithAuth } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('info'); // info, botanik, botaniq, admin
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isImageExpanded, setIsImageExpanded] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [localPlant, setLocalPlant] = useState(plant);

    const isAdmin = user && (user.is_staff || user.is_superuser);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    if (!plant) return null;

    const data = userPlantData[localPlant.id] || { learning_status: 'NOT_LEARNED', is_favorite: false };
    const category = categories.find(c => c.id === localPlant.category);
    const images = localPlant.images || [];
    const hasMultipleImages = images.length > 1;

    const nextImage = (e) => {
        e?.stopPropagation();
        if (hasMultipleImages) setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        if (hasMultipleImages) setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Modal Overlay
    const handleOverlayClick = () => onClose();

    const handleExportPdf = async () => {
        if (!plant || isExporting) return;
        setIsExporting(true);

        try {
            // 1. Create UserPlantOfTheWeek entry
            const createRes = await fetchWithAuth('/api/apprentice/pflanze-der-woche/create_from_plant/', {
                method: 'POST',
                body: JSON.stringify({ plant_id: plant.id })
            });

            if (!createRes.ok) throw new Error('Fehler beim Erstellen des Eintrags');
            const entry = await createRes.json();

            // 2. Trigger PDF Download
            const pdfUrl = `${API_URL}/api/apprentice/pflanze-der-woche/${entry.id}/download_pdf/`;
            const pdfRes = await fetchWithAuth(pdfUrl);
            if (!pdfRes.ok) throw new Error('Fehler beim PDF Download');

            const blob = await pdfRes.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Pflanze_der_Woche_${plant.botanical_name.replace(/ /g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error("Export error:", error);
            alert("Export fehlgeschlagen: " + error.message);
        } finally {
            setIsExporting(false);
        }
    };

    const isEligibleForExport = user && (user.is_staff || user.user_profile?.is_community_member);

    // Handle admin save: merge updated fields into localPlant so modal reflects changes immediately
    const handleAdminSaved = (updatedPlant) => {
        if (updatedPlant) {
            setLocalPlant(prev => ({ ...prev, ...updatedPlant }));
        } else {
            // Image change - reload plant images from server
            fetchWithAuth(`/api/plants/${localPlant.id}/`)
                .then(r => r.json())
                .then(p => setLocalPlant(p))
                .catch(() => {});
        }
    };

    const modalContent = (
        <div className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center sm:p-6 animate-fadeIn">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleOverlayClick} />

            {/* Modal Card */}
            <div className="relative w-full max-w-6xl bg-white dark:bg-[#1e1e1e] rounded-t-[2rem] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:flex-row h-[calc(100dvh-3rem)] sm:h-[85vh] sm:max-h-[900px] mt-auto sm:mt-0 animate-slideUp sm:animate-scaleIn ring-1 ring-white/10">

                {/* --- LEFT COLUMN: Image Gallery (Mobile: Top 25vh, Desktop: Half) --- */}
                <div className="w-full sm:w-1/2 h-[35dvh] min-h-[250px] sm:h-full relative bg-gray-900 group flex-shrink-0">
                    {images.length > 0 ? (
                        <>
                            <div className="absolute inset-0 transition-transform duration-500 cursor-zoom-in" onClick={() => setIsImageExpanded(true)}>
                                <OptimizedImage
                                    src={images[currentImageIndex].image_medium || images[currentImageIndex].image}
                                    alt={plant.botanical_name}
                                    className="w-full h-full object-cover"
                                    priority={true}
                                />
                                {/* Minimal Gradient - Only bottom for names */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent sm:from-black/30 sm:via-transparent sm:to-transparent" />
                            </div>

                            {/* Navigation Arrows - Large Touch Targets */}
                            {hasMultipleImages && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-md transition active:scale-95 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                        aria-label="Vorheriges Bild"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-md transition active:scale-95 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                        aria-label="Nächstes Bild"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                    </button>

                                    {/* Dots Indicator - Mobile Always Visible */}
                                    <div className="absolute bottom-20 sm:bottom-4 left-0 right-0 flex justify-center gap-2">
                                        {images.map((_, idx) => (
                                            <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} />
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500">
                            <LeafIcon className="w-24 h-24 opacity-20" />
                        </div>
                    )}

                    {/* Plant Names - Mobile: Bottom of Image, Desktop: Overlay */}
                    <div className="absolute bottom-4 left-0 right-0 p-6 sm:bottom-12 sm:left-8 pointer-events-none">
                        <div className="sm:hidden bg-gradient-to-t from-black/90 via-black/50 to-transparent -mx-6 -mb-10 px-6 pb-12 pt-12">
                            <h2 className="text-2xl font-bold text-white leading-tight mb-1 drop-shadow-md">
                                {plant.german_name}
                            </h2>
                            <p className="text-sm text-gray-200 italic drop-shadow">
                                {plant.botanical_name}
                            </p>
                        </div>

                        {/* Desktop: Full Overlay */}
                        <div className="hidden sm:block">
                            {category && (
                                <span className="inline-block px-2.5 py-0.5 rounded-md bg-white/20 backdrop-blur-md text-white/90 text-[10px] uppercase font-bold tracking-wider mb-2 border border-white/10">
                                    {category.name}
                                </span>
                            )}
                            <h2 className="text-4xl font-bold text-white leading-tight drop-shadow-md">
                                {plant.botanical_name}
                            </h2>
                            <p className="text-lg text-gray-300 font-medium italic mt-1 drop-shadow-sm">
                                {plant.german_name}
                            </p>
                        </div>
                    </div>

                    {/* Top Controls - Minimal, Transparent */}
                    <div className="absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 flex gap-2 z-20">
                        <SharePlantButton plant={plant} />
                        {isEligibleForExport && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleExportPdf(); }}
                                className="w-11 h-11 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm border border-white/5 transition-none active:bg-black/60 text-white/70"
                                title="Als 'Pflanze der Woche' PDF exportieren"
                                disabled={isExporting}
                            >
                                {isExporting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); onUpdateUserPlantData(plant.id, { is_favorite: !data.is_favorite }); }}
                            className="w-11 h-11 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 transition-none active:bg-black/80"
                            aria-label={data.is_favorite ? "Von Favoriten entfernen" : "Zu Favoriten hinzufügen"}
                        >
                            <StarIcon filled={data.is_favorite} className={`w-5 h-5 ${data.is_favorite ? "text-amber-400" : "text-white/90"}`} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            className="w-11 h-11 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 transition-none text-white/90 active:bg-black/80"
                            aria-label="Schließen"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    {/* Status Badge - Smaller, Top Left */}
                    <div className="absolute top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-20">
                        <LearningStatusBadge
                            status={data.learning_status}
                            size="sm"
                            plantId={plant.id}
                            onUpdateStatus={onUpdateUserPlantData}
                            className="shadow-md !bg-black/40 !backdrop-blur-md border !border-white/10 text-white text-xs"
                        />
                    </div>
                </div>

                {/* --- RIGHT COLUMN: Scrollable Content --- */}
                <div className="w-full sm:w-1/2 flex flex-col bg-white dark:bg-[#1e1e1e] flex-1 min-h-0 sm:h-full relative rounded-t-[2rem] sm:rounded-none -mt-6 sm:mt-0 z-10 sm:z-auto border-t border-white/10 sm:border-none shadow-[0_-8px_30px_rgba(0,0,0,0.12)] sm:shadow-none">
                    
                    {/* Mobile Handle */}
                    <div className="w-full flex justify-center pt-3 pb-1 sm:hidden shrink-0">
                        <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex border-b border-gray-100 dark:border-white/5 px-6 pt-1 sm:pt-2 shrink-0 overflow-x-auto scrollbar-none">
                        <button
                            onClick={() => setActiveTab('info')}
                            className={`py-4 mr-6 text-sm font-bold tracking-wide border-b-2 whitespace-nowrap transition-colors ${activeTab === 'info'
                                ? 'border-plantlix-500 text-plantlix-600 dark:text-plantlix-400'
                                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                        >
                            ÜBERSICHT
                        </button>
                        <button
                            onClick={() => setActiveTab('botanik')}
                            className={`py-4 mr-6 text-sm font-bold tracking-wide border-b-2 whitespace-nowrap transition-colors ${activeTab === 'botanik'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                        >
                            BOTANIK
                        </button>
                        <button
                            onClick={() => setActiveTab('botaniq')}
                            className={`py-4 mr-6 text-sm font-bold tracking-wide border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${activeTab === 'botaniq'
                                ? 'border-plantlix-500 text-plantlix-500 dark:text-blue-400'
                                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                        >
                            <span>BOTANIQ AI</span>
                            <span className="bg-plantlix-100 dark:bg-plantlix-900 text-plantlix-500 dark:text-blue-300 text-[9px] px-1.5 py-0.5 rounded uppercase font-bold">{appVersion}</span>
                        </button>
                        {isAdmin && (
                            <button
                                onClick={() => setActiveTab('admin')}
                                className={`py-4 text-sm font-bold tracking-wide border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 ${activeTab === 'admin'
                                    ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                                    : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                    }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                BEARBEITEN
                            </button>
                        )}
                    </div>

                    {/* Scrollable Area */}
                    <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
                        {activeTab === 'admin' && isAdmin ? (
                            <AdminPlantEditor
                                plant={localPlant}
                                categories={categories}
                                fetchWithAuth={fetchWithAuth}
                                onSaved={handleAdminSaved}
                                onClose={() => setActiveTab('info')}
                            />
                        ) : activeTab === 'info' ? (
                            <div className="space-y-6 animate-fadeIn">
                                {/* Mobile: Category Badge */}
                                <div className="sm:hidden">
                                    {category && (
                                        <span className="inline-block px-3 py-1 rounded-lg bg-plantlix-100 dark:bg-plantlix-900/30 text-plantlix-700 dark:text-plantlix-300 text-xs font-bold tracking-wide border border-plantlix-200 dark:border-plantlix-800">
                                            {category.name}
                                        </span>
                                    )}
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-3">{plant.german_name}</h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">{plant.botanical_name}</p>
                                </div>

                                {/* Description - Prominent */}
                                {(plant.description || plant.shortened_data?.notes) && (
                                    <section className="bg-gradient-to-br from-plantlix-50 to-plantlix-50 dark:from-plantlix-900/20 dark:to-plantlix-900/20 p-5 rounded-2xl border border-plantlix-200 dark:border-plantlix-800/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-plantlix-600 dark:text-plantlix-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h3 className="text-sm uppercase font-bold text-plantlix-700 dark:text-plantlix-400 tracking-wider">Beschreibung</h3>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-200 leading-relaxed text-[15px] whitespace-pre-line">
                                            <TextWithExpand
                                                short={plant.shortened_data?.notes || plant.description}
                                                full={plant.description}
                                            />
                                        </p>
                                    </section>
                                )}

                                {/* Origin & Distribution - Highlighted */}
                                {(plant.origin || plant.distribution || plant.shortened_data?.origin || plant.shortened_data?.distribution) && (
                                    <section className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-plantlix-900/20 dark:to-cyan-900/20 p-5 rounded-2xl border border-blue-200 dark:border-blue-800/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-plantlix-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h3 className="text-sm uppercase font-bold text-blue-700 dark:text-blue-400 tracking-wider">Herkunft &amp; Verbreitung</h3>
                                        </div>
                                        <div className="space-y-3">
                                            {(plant.origin || plant.shortened_data?.origin) && (
                                                <div>
                                                    <div className="text-xs uppercase font-bold text-plantlix-500 dark:text-blue-400 mb-1">📍 Herkunft</div>
                                                    <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed">
                                                        <TextWithExpand short={plant.shortened_data?.origin || plant.origin} full={plant.origin} />
                                                    </p>
                                                </div>
                                            )}
                                            {(plant.distribution || plant.shortened_data?.distribution) && (
                                                <div>
                                                    <div className="text-xs uppercase font-bold text-plantlix-500 dark:text-blue-400 mb-1">🗺️ Verbreitung</div>
                                                    <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed">
                                                        <TextWithExpand short={plant.shortened_data?.distribution || plant.distribution} full={plant.distribution} />
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {/* Quick Facts Grid */}
                                <section>
                                    <h3 className="text-xs uppercase font-bold text-gray-400 mb-4 tracking-wider flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                        Eigenschaften
                                    </h3>
                                    <PlantAttributeGrid plant={plant} />
                                </section>

                                {/* Family */}
                                <section className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/30">
                                    <h4 className="text-xs uppercase font-bold text-purple-700 dark:text-purple-400 mb-2 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                        </svg>
                                        Familie
                                    </h4>
                                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{plant.family || 'Keine Angabe'}</p>
                                </section>

                                {/* Usage */}
                                {plant.usage && (
                                    <section className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-5 rounded-2xl border border-amber-200 dark:border-amber-800/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                            </svg>
                                            <h3 className="text-sm uppercase font-bold text-amber-700 dark:text-amber-400 tracking-wider">Verwendung</h3>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-200 leading-relaxed text-sm">
                                            {plant.usage}
                                        </p>
                                    </section>
                                )}

                                {/* Care Notes */}
                                {(plant.care_notes || plant.shortened_data?.care_notes || plant.watering_needs || plant.fertilizer_needs) && (
                                    <section className="bg-gradient-to-br from-plantlix-50 to-cyan-50 dark:from-plantlix-900/20 dark:to-cyan-900/20 p-5 rounded-2xl border border-plantlix-200 dark:border-plantlix-800/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-plantlix-600 dark:text-plantlix-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h3 className="text-sm uppercase font-bold text-plantlix-700 dark:text-plantlix-400 tracking-wider">Pflege-Tipps</h3>
                                        </div>
                                        <div className="space-y-3">
                                            {(plant.care_notes || plant.shortened_data?.care_notes) && (
                                                <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed whitespace-pre-line">
                                                    <TextWithExpand short={plant.shortened_data?.care_notes || plant.care_notes} full={plant.care_notes} />
                                                </p>
                                            )}
                                            {plant.watering_needs && (
                                                <div className="flex items-start gap-2">
                                                    <span className="text-plantlix-600 dark:text-plantlix-400 font-semibold text-sm">💧 Wasser:</span>
                                                    <span className="text-gray-700 dark:text-gray-200 text-sm">{plant.watering_needs}</span>
                                                </div>
                                            )}
                                            {plant.fertilizer_needs && (
                                                <div className="flex items-start gap-2">
                                                    <span className="text-plantlix-600 dark:text-plantlix-400 font-semibold text-sm">🌱 Dünger:</span>
                                                    <span className="text-gray-700 dark:text-gray-200 text-sm">{plant.fertilizer_needs}</span>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </div>
                        ) : activeTab === 'botanik' ? (
                            <div className="space-y-8 animate-fadeIn">
                                {/* Artname-Erklärung */}
                                {(plant.artname_erklärung || plant.shortened_data?.artname_erklärung) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🔬 Artname-Erklärung</h3>
                                        <div className="p-4 rounded-xl bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30">
                                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                <TextWithExpand
                                                    short={plant.shortened_data?.artname_erklärung || plant.artname_erklärung}
                                                    full={plant.artname_erklärung}
                                                />
                                            </p>
                                        </div>
                                    </section>
                                )}

                                {/* Blätter */}
                                {(plant.leaves || plant.shortened_data?.leaves) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🍃 Blätter</h3>
                                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                <TextWithExpand
                                                    short={plant.shortened_data?.leaves || plant.leaves}
                                                    full={plant.leaves}
                                                />
                                            </p>
                                        </div>
                                    </section>
                                )}

                                {/* Früchte */}
                                {(plant.fruits || plant.shortened_data?.fruits) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🍒 Früchte</h3>
                                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                <TextWithExpand
                                                    short={plant.shortened_data?.fruits || plant.fruits}
                                                    full={plant.fruits}
                                                />
                                            </p>
                                        </div>
                                    </section>
                                )}

                                {/* Rinde */}
                                {(plant.bark_description || plant.shortened_data?.bark_description) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🌳 Rinde</h3>
                                        <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                <TextWithExpand
                                                    short={plant.shortened_data?.bark_description || plant.bark_description}
                                                    full={plant.bark_description}
                                                />
                                            </p>
                                        </div>
                                    </section>
                                )}

                                {/* Herkunft */}
                                {(plant.origin || plant.distribution) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🌍 Herkunft & Verbreitung</h3>
                                        <div className="space-y-2">
                                            {plant.origin && (
                                                <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-plantlix-900/10 border border-plantlix-100/50 dark:border-plantlix-900/30">
                                                    <div className="text-xs uppercase font-bold text-plantlix-500 dark:text-blue-400 mb-1">Herkunft</div>
                                                    <p className="text-gray-700 dark:text-gray-300 text-sm">{plant.origin}</p>
                                                </div>
                                            )}
                                            {plant.distribution && (
                                                <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-plantlix-900/10 border border-plantlix-100/50 dark:border-plantlix-900/30">
                                                    <div className="text-xs uppercase font-bold text-plantlix-500 dark:text-blue-400 mb-1">Verbreitung</div>
                                                    <p className="text-gray-700 dark:text-gray-300 text-sm">{plant.distribution}</p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {/* Besonderheiten */}
                                {(plant.is_poisonous || plant.is_edible || plant.is_heimisch || plant.is_protected || plant.special_features) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">⚠️ Besonderheiten</h3>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {plant.is_heimisch && (
                                                <span className="px-3 py-1.5 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 text-plantlix-700 dark:text-plantlix-300 text-xs font-semibold border border-plantlix-200 dark:border-plantlix-800">
                                                    🇩🇪 Heimisch
                                                </span>
                                            )}
                                            {plant.is_protected && (
                                                <span className="px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold border border-amber-200 dark:border-amber-800">
                                                    🛡️ Geschützt
                                                </span>
                                            )}
                                            {plant.is_poisonous && (
                                                <span className="px-3 py-1.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold border border-red-200 dark:border-red-800">
                                                    ☠️ Giftig
                                                </span>
                                            )}
                                            {plant.is_edible && (
                                                <span className="px-3 py-1.5 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 text-plantlix-700 dark:text-plantlix-300 text-xs font-semibold border border-plantlix-200 dark:border-plantlix-800">
                                                    🍴 Essbar
                                                </span>
                                            )}
                                        </div>
                                        {plant.special_features && (
                                            <div className="p-4 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30">
                                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                    {plant.special_features}
                                                </p>
                                            </div>
                                        )}
                                    </section>
                                )}

                                {/* Lebensbereich & Geselligkeit */}
                                {(plant.life_zone || plant.sociality) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">🌿 Ökologische Eigenschaften</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {plant.life_zone && (
                                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                                    <div className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-1">Lebensbereich</div>
                                                    <p className="text-gray-900 dark:text-white text-sm font-medium">{plant.life_zone}</p>
                                                </div>
                                            )}
                                            {plant.sociality && (
                                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                                    <div className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-1">Geselligkeit</div>
                                                    <p className="text-gray-900 dark:text-white text-sm font-medium">{plant.sociality}</p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {/* Pflege-Hinweise */}
                                {(plant.care_notes || plant.pruning_notes) && (
                                    <section>
                                        <h3 className="text-xs uppercase font-bold text-gray-400 mb-3 tracking-wider">✂️ Pflege</h3>
                                        <div className="space-y-3">
                                            {plant.care_notes && (
                                                <div className="p-4 rounded-xl bg-plantlix-50/50 dark:bg-plantlix-900/10 border border-plantlix-100 dark:border-plantlix-900/30">
                                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm whitespace-pre-line">
                                                        {plant.care_notes}
                                                    </p>
                                                </div>
                                            )}
                                            {plant.pruning_notes && (
                                                <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                                                    <div className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-2">Schnitt</div>
                                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm whitespace-pre-line">
                                                        {plant.pruning_notes}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </div>
                        ) : (
                            <div className="h-full animate-fadeIn pb-6">
                                <BotaniqChat plant={plant} />
                            </div>
                        )}

                        {/* Footer Spacer */}
                        <div className="h-10"></div>
                    </div>
                </div>
            </div>

            {/* Fullscreen Image Viewer with Navigation */}
            {isImageExpanded && (
                <div
                    className="fixed inset-0 z-[100] bg-black flex items-center justify-center animate-fadeIn"
                    onClick={() => setIsImageExpanded(false)}
                >
                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-none active:bg-white/30 z-50"
                        onClick={(e) => { e.stopPropagation(); setIsImageExpanded(false); }}
                        aria-label="Schließen"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Image Container */}
                    <div className="w-full h-full max-w-[95vw] max-h-[95vh] relative flex items-center justify-center">
                        <OptimizedImage
                            src={images[currentImageIndex].image}
                            alt={plant.botanical_name}
                            className="max-w-full max-h-full object-contain shadow-2xl"
                            priority={true}
                        />
                    </div>

                    {/* Navigation Arrows in Fullscreen */}
                    {hasMultipleImages && (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); prevImage(e); }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-none active:bg-white/30 z-50"
                                aria-label="Vorheriges Bild"
                            >
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); nextImage(e); }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-none active:bg-white/30 z-50"
                                aria-label="Nächstes Bild"
                            >
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            {/* Image Counter */}
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 backdrop-blur-md text-white text-sm font-medium">
                                {currentImageIndex + 1} / {images.length}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default PlantDetailModal;
